import React from "react";
import { TopBar } from "@/components/TopBar";
import { ROLES, ROLE_LABELS, KARYAWAN } from "@/lib/roles";
import {
  apiListUsers,
  apiCreateUser,
  apiUpdateUser,
  apiPekerjaanUser,
  apiResetPassword,
  apiListSampahAkun,
  apiHapusAkun,
  apiPulihkanAkun,
  apiHapusPermanenAkun,
  apiBukaKunci,
  formatApiErrorDetail,
} from "@/lib/api";
import { alasanValid, bolehHapus, labelDihapus, teksJejak } from "@/lib/sampahAkun";
import * as W from "@/lib/waktu";
import {
  UserPlus,
  Loader2,
  KeyRound,
  Power,
  Pencil,
  Check,
  X,
  RefreshCw,
  Trash2,
  Undo2,
  LockOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AlihkanPanel from "@/components/AlihkanPanel";

const EMPTY_FORM = {
  nama: "",
  username: "",
  password: "",
  role: "kasir",
  is_active: true,
};

export default function UserManagement() {
  const { user: me } = useAuth();
  // owner 2026-10-12: a Manager manages employee accounts only (operators, Desain, Kasir/CS); the server enforces it
  const isManager = me?.role === "manager";
  const pilihanRole = isManager ? ROLES.filter((r) => KARYAWAN.includes(r)) : ROLES;
  const bolehKelola = (u) => !isManager || (KARYAWAN.includes(u.role) && u.id !== me?.id);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  // T2-A: before deactivating an operator, show the work it still holds
  const [nonaktif, setNonaktif] = React.useState(null);  // {u, rows}
  const [notice, setNotice] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [creating, setCreating] = React.useState(false);
  // v2.14 (requirement 75 butir 3): Sampah akun — the server decides every right; this only mirrors it
  const [tab, setTab] = React.useState("aktif");
  const [sampah, setSampah] = React.useState([]);
  const [hapus, setHapus] = React.useState(null);  // {u, rows} — confirmation before moving to the Sampah
  const [alasan, setAlasan] = React.useState("");
  const [sibuk, setSibuk] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsers(await apiListUsers());
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSampah = React.useCallback(async () => {
    try {
      setSampah(await apiListSampahAkun());
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  React.useEffect(() => {
    load();
    loadSampah();
  }, [load, loadSampah]);

  const mulaiHapus = async (u) => {
    setError(null);
    try {
      const rows = await apiPekerjaanUser(u.id);
      setNonaktif(null);
      setHapus({ u, rows });
      setAlasan("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const jalankan = async (fn, pesan) => {
    setError(null);
    setSibuk(true);
    try {
      await fn();
      flash(pesan);
      setHapus(null);
      setAlasan("");
      await Promise.all([load(), loadSampah()]);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSibuk(false);
    }
  };

  const keSampah = () => jalankan(() => apiHapusAkun(hapus.u.id, alasan.trim()), `${hapus.u.username} dipindahkan ke Sampah`);

  const pulihkan = (u) => {
    const a = window.prompt(`Alasan memulihkan ${u.username} (wajib). Akun pulih sebagai NONAKTIF; aktifkan terpisah, dan pemiliknya wajib mengganti password pada login berikutnya.`);
    if (!a) return;
    if (!alasanValid(a)) { setError("Alasan minimal 3 huruf"); return; }
    jalankan(() => apiPulihkanAkun(u.id, a.trim()), `${u.username} dipulihkan sebagai Nonaktif`);
  };

  const hapusPermanen = (u) => {
    const a = window.prompt(`HAPUS PERMANEN ${u.username}? Tidak bisa dibatalkan. Hanya akun yang belum pernah dipakai; server memeriksanya lagi. Tulis alasan (wajib):`);
    if (!a) return;
    if (!alasanValid(a)) { setError("Alasan minimal 3 huruf"); return; }
    jalankan(() => apiHapusPermanenAkun(u.id, a.trim()), `${u.username} dihapus permanen (tercatat di audit log)`);
  };

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 2500);
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      await apiCreateUser({ ...form, username: form.username.trim() });
      setForm(EMPTY_FORM);
      flash("User berhasil ditambahkan");
      await load();
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u, paksa = false) => {
    setError(null);
    try {
      if (u.is_active && !paksa) {
        const rows = await apiPekerjaanUser(u.id);
        if (rows.length) { setNonaktif({ u, rows }); return; }
      }
      await apiUpdateUser(u.id, { is_active: !u.is_active });
      setNonaktif(null);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // nama tampil (mis. akun bawaan "Operator DTF (belum ditentukan)" -> nama karyawan)
  const rename = async (u) => {
    const nama = window.prompt(`Nama baru untuk ${u.username}:`, u.nama);
    if (!nama || !nama.trim() || nama.trim() === u.nama) return;
    setError(null);
    try {
      await apiUpdateUser(u.id, { nama: nama.trim() });
      flash(`Nama ${u.username} diubah menjadi ${nama.trim()}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const ubahLayar = async (u) => {
    setError(null);
    try {
      await apiUpdateUser(u.id, { layar_produksi: !u.layar_produksi_efektif });
      flash(`Izin layar produksi ${u.username}: ${!u.layar_produksi_efektif ? "boleh" : "tidak"} (tercatat di audit log)`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const changeRole = async (u, role) => {
    setError(null);
    try {
      await apiUpdateUser(u.id, { role });
      flash(`Role ${u.username} diperbarui`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`Password SEMENTARA baru untuk ${u.username} (min 8 karakter). Berikan langsung ke karyawan; ia bisa (atau wajib, bila pengaturan nyala) menggantinya sendiri. Login lama ${u.username} akan keluar.`);
    if (!pw) return;
    setError(null);
    try {
      await apiResetPassword(u.id, pw);
      flash(`Password sementara ${u.username} disimpan (tercatat di audit log)`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="user-management-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <div>
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100"
          >
            Manajemen User
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Hanya admin yang dapat mengelola user. Password selalu disimpan
            ter-hash.
          </p>
        </div>

        {notice && (
          <div
            data-testid="um-notice"
            className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300"
          >
            {notice}
          </div>
        )}
        {nonaktif && (
          <div data-testid="nonaktif-pekerjaan" className="rounded-xl border border-amber-500/40 bg-slate-900/70 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-100">{nonaktif.u.nama || nonaktif.u.username} masih memegang {nonaktif.rows.length} pekerjaan</h3>
            <p className="text-xs text-slate-400">Alihkan ke operator aktif lain sebelum menonaktifkan, atau nonaktifkan sekarang dan alihkan nanti dari Antrean Produksi (tombol Alihkan).</p>
            {nonaktif.rows.map((r) => (
              <AlihkanPanel key={`${r.spk_id}-${r.urutan}`} item={{ ...r, started_by_username: nonaktif.u.username }}
                onDone={async () => setNonaktif({ ...nonaktif, rows: await apiPekerjaanUser(nonaktif.u.id) })} />
            ))}
            <div className="flex gap-2">
              <button data-testid="nonaktif-lanjut" onClick={() => toggleActive(nonaktif.u, true)} className="rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-slate-950">Nonaktifkan {nonaktif.rows.length ? "sekarang (pekerjaan tetap tertahan)" : "sekarang"}</button>
              <button data-testid="nonaktif-batal" onClick={() => setNonaktif(null)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300">Batal</button>
            </div>
          </div>
        )}
        {hapus && (
          <div data-testid="hapus-panel" className="rounded-xl border border-red-500/40 bg-slate-900/70 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-slate-100">Pindahkan {hapus.u.nama || hapus.u.username} ({hapus.u.username}) ke Sampah?</h3>
            <p className="text-xs text-slate-400">Akun hilang dari daftar dan semua pilihan, tidak bisa login, semua sesinya langsung putus. Riwayat nota, pembayaran, upah, dan produksi tetap utuh; username tidak bisa dipakai ulang. Bisa dipulihkan dari tab Sampah (sebagai Nonaktif).</p>
            {hapus.rows.length > 0 && <p data-testid="hapus-pekerjaan" className="text-xs text-amber-300">Masih memegang {hapus.rows.length} pekerjaan: {hapus.rows.map((r) => `${r.nomor_spk} (${r.pos_nama || r.pos_code})`).join(", ")}. Alihkan dari Antrean Produksi (tombol Alihkan) sebelum atau sesudah dihapus.</p>}
            <label className="block text-xs text-slate-300">Alasan (wajib)
              <input data-testid="hapus-alasan" value={alasan} onChange={(e) => setAlasan(e.target.value)} maxLength={300} placeholder="mis. karyawan berhenti" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60" />
            </label>
            <div className="flex gap-2">
              <button data-testid="hapus-lanjut" disabled={sibuk || !alasanValid(alasan)} onClick={keSampah} className="rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-40">Pindahkan ke Sampah</button>
              <button data-testid="hapus-batal" onClick={() => setHapus(null)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300">Batal</button>
            </div>
          </div>
        )}
        {error && (
          <div
            data-testid="um-error"
            className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300"
          >
            {error}
          </div>
        )}

        {/* Add user form */}
        <form
          data-testid="create-user-form"
          onSubmit={onCreate}
          className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
        >
          <div className="lg:col-span-1">
            <label className="text-[13px] font-semibold text-slate-200">
              Nama
            </label>
            <input
              data-testid="create-nama-input"
              value={form.nama}
              onChange={(e) => setForm({ ...form, nama: e.target.value })}
              required
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60"
            />
          </div>
          <div>
            <label className="text-[13px] font-semibold text-slate-200">
              Username
            </label>
            <input
              data-testid="create-username-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              minLength={3}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60"
            />
          </div>
          <div>
            <label className="text-[13px] font-semibold text-slate-200">
              Password
            </label>
            <input
              data-testid="create-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60"
            />
          </div>
          <div>
            <label className="text-[13px] font-semibold text-slate-200">
              Role
            </label>
            <select
              data-testid="create-role-select"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60"
            >
              {pilihanRole.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              data-testid="create-user-submit"
              type="submit"
              disabled={creating}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 transition-all hover:bg-amber-400 disabled:opacity-60"
            >
              {creating ? <Loader2 className="animate-spin" size={16} /> : <UserPlus size={16} />}
              Tambah
            </button>
          </div>
        </form>

        {/* Users table */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <button data-testid="tab-aktif" onClick={() => setTab("aktif")} className={`text-[15px] font-bold px-2 py-1 rounded-md ${tab === "aktif" ? "text-soq-biru-tua bg-amber-500/10" : "text-slate-400"}`}>Daftar User</button>
              <button data-testid="tab-sampah" onClick={() => setTab("sampah")} className={`inline-flex items-center gap-1 text-[15px] font-bold px-2 py-1 rounded-md ${tab === "sampah" ? "text-soq-biru-tua bg-amber-500/10" : "text-slate-400"}`}><Trash2 size={14} /> Sampah ({sampah.length})</button>
            </div>
            <button
              data-testid="refresh-users-button"
              onClick={() => { load(); loadSampah(); }}
              className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {tab === "sampah" ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sampah-table">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                    <th className="px-5 py-3">Nama</th>
                    <th className="px-5 py-3">Username</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3">Dihapus</th>
                    <th className="px-5 py-3">Pemakaian</th>
                    <th className="px-5 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {sampah.map((u) => (
                    <tr key={u.id} data-testid={`sampah-row-${u.username}`} className="border-b border-slate-800/60">
                      <td className="px-5 py-3 text-slate-200">{u.nama}</td>
                      <td className="px-5 py-3 font-mono text-slate-300">{u.username}</td>
                      <td className="px-5 py-3 text-slate-300">{ROLE_LABELS[u.role] || u.role}</td>
                      <td className="px-5 py-3 text-xs text-slate-400">{labelDihapus(u, W.waktu)}</td>
                      <td className="px-5 py-3 text-xs text-slate-400" data-testid={`jejak-${u.username}`}>{teksJejak(u.jejak)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`pulihkan-${u.username}`} disabled={sibuk} onClick={() => pulihkan(u)} title="Pulihkan (sebagai Nonaktif)" className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300"><Undo2 size={13} /> Pulihkan</button>
                          {me?.role === "admin" && <button data-testid={`hapus-permanen-${u.username}`} disabled={sibuk || !u.bisa_hapus_permanen} onClick={() => hapusPermanen(u)} title={u.bisa_hapus_permanen ? "Hapus permanen (tidak bisa dibatalkan)" : "Tidak bisa: " + teksJejak(u.jejak)} className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-red-500/40 hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={13} /> Hapus permanen</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {sampah.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">Sampah kosong.</td></tr>
                  )}
                </tbody>
              </table>
              <p className="px-5 py-3 text-xs text-slate-500">Pulihkan = akun kembali sebagai Nonaktif; saat diaktifkan, pemiliknya wajib mengganti password. Hapus permanen hanya Owner, hanya akun yang belum pernah dipakai (diperiksa server); akun yang dibuat sebelum v2.14 tidak bisa dihapus permanen.</p>
            </div>
          ) : loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400">
              <Loader2 className="animate-spin mr-2" size={18} /> Memuat…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                    <th className="px-5 py-3">Nama</th>
                    <th className="px-5 py-3">Username</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3" title="v2.15: boleh membuka /monitoring/layar dengan login (mis. dari rumah). Bawaan: Owner dan Manager. Hanya Owner yang mengubah.">Layar produksi</th>
                    <th className="px-5 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      data-testid={`user-row-${u.username}`}
                      className="border-b border-slate-800/60 hover:bg-slate-800/30"
                    >
                      <td className="px-5 py-3 text-slate-200">{u.nama}</td>
                      <td className="px-5 py-3 font-mono text-slate-300">
                        {u.username}
                        {u.password_sementara && <span data-testid={`pw-sementara-${u.username}`} title="Password masih dari Owner (belum diganti sendiri)" className="ml-2 rounded border border-amber-500/40 px-1.5 text-[10px] text-amber-300">password sementara</span>}
                        {u.administrator && <span data-testid={`administrator-${u.username}`} title="Ditandai dari server (ADMIN_TEKNIS)" className="ml-2 rounded border border-sky-500/40 px-1.5 text-[10px] text-sky-300">Administrator</span>}
                      </td>
                      <td className="px-5 py-3">
                        <select
                          data-testid={`role-select-${u.username}`}
                          value={u.role}
                          disabled={!bolehKelola(u)}
                          onChange={(e) => changeRole(u, e.target.value)}
                          className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200 outline-none focus:border-amber-500/60"
                        >
                          {(bolehKelola(u) ? pilihanRole : ROLES).map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          data-testid={`status-${u.username}`}
                          className={`inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-md border ${
                            u.is_active
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                              : "border-slate-600 bg-slate-700/30 text-slate-400"
                          }`}
                        >
                          {u.is_active ? <Check size={12} /> : <X size={12} />}
                          {u.is_active ? "Aktif" : "Nonaktif"}
                        </span>
                        {/* v2.21.1 (owner 2026-10-26): too many failed logins → locked 15 minutes; Owner/Manager open it */}
                        {u.terkunci_sampai && <div data-testid={`login-terkunci-${u.username}`} className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-red-300">
                          Login terkunci s/d {W.jam(u.terkunci_sampai)}
                          {bolehKelola(u) && <button type="button" data-testid={`buka-kunci-${u.username}`} disabled={sibuk}
                            onClick={() => jalankan(() => apiBukaKunci(u.id), `Kunci login ${u.username} dibuka`)}
                            className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-red-500/40 px-2 py-0.5 text-red-300 hover:bg-red-500/10 disabled:opacity-40">
                            <LockOpen size={12} /> Buka kunci
                          </button>}
                        </div>}
                      </td>
                      <td className="px-5 py-3">
                        {/* v2.15 (owner 2026-10-22): the switch "Boleh lihat layar produksi" — Owner only; the server enforces it */}
                        {me?.role === "admin" && u.role !== "admin" ? (
                          <label className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                            <input data-testid={`layar-${u.username}`} type="checkbox" checked={!!u.layar_produksi_efektif}
                              onChange={() => ubahLayar(u)} />
                            {u.layar_produksi_efektif ? "boleh" : "tidak"}{u.layar_produksi === null || u.layar_produksi === undefined ? <span className="text-slate-500">(bawaan)</span> : null}
                          </label>
                        ) : <span data-testid={`layar-${u.username}`} className="text-xs text-slate-400">{u.layar_produksi_efektif ? "boleh" : "tidak"}{u.role === "admin" ? " (Owner)" : ""}</span>}
                      </td>
                      <td className="px-5 py-3">
                        {bolehKelola(u) ? <div className="flex items-center justify-end gap-2">
                          <button
                            data-testid={`ubah-nama-${u.username}`}
                            onClick={() => rename(u)}
                            title="Ubah nama"
                            className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            data-testid={`reset-pw-${u.username}`}
                            onClick={() => resetPw(u)}
                            title="Reset password"
                            className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300"
                          >
                            <KeyRound size={13} />
                          </button>
                          <button
                            data-testid={`toggle-active-${u.username}`}
                            onClick={() => toggleActive(u)}
                            disabled={u.id === me?.id}
                            title={u.id === me?.id ? "Tidak bisa menonaktifkan diri sendiri" : "Aktif/Nonaktif"}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-red-500/40 hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Power size={13} />
                          </button>
                          {bolehHapus(me, u) && <button
                            data-testid={`hapus-${u.username}`}
                            onClick={() => mulaiHapus(u)}
                            title="Hapus (pindahkan ke Sampah)"
                            className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-red-500/40 hover:text-red-300"
                          >
                            <Trash2 size={13} />
                          </button>}
                        </div> : <span data-testid={`user-terkunci-${u.username}`} className="block text-right text-xs text-slate-500">hanya Owner</span>}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-center text-slate-500">
                        Belum ada user.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
